import { Box, List, Pagination, Stack, Typography } from '@mui/material';
import { Container } from '@mui/system';
import { createSelector } from '@reduxjs/toolkit';
import { AppState, ProductListConfig, ProductListResultContainer, apiGet, idFromConfig, productListSlice } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { appStore } from '../../store';
import { MasterViewPage } from '../MasterPage';
import { LoadingBackdrop } from '../utils/LoadingBackdrop';
import { ProductListItem } from './ProductListItem';

const getResultsByQueryId = createSelector([
    (state: AppState) => state.productList.resultsPerQuery,
    (_: AppState, queryId?: string) => queryId
], (resultsPerQuery: ProductListResultContainer, queryId?: string) => {
    if (queryId === undefined) return undefined;
    return resultsPerQuery[queryId];
});
/**
 * 
 * @returns Vue générale pour afficher une liste de produit
 */
export const ProductList = (props: {
    fixedProps?: Partial<ProductListConfig>;
}) => {

    const [currConfig, setCurrConfig] = React.useState<ProductListConfig | undefined>(undefined);
    const [currQueryId, setCurrQueryId] = React.useState<string | undefined>(undefined);
    const results = useSelector((state: AppState) => getResultsByQueryId(state, currQueryId));
    const offset = useSelector((state: AppState) => state.productList.offset);
    const limit = useSelector((state: AppState) => state.productList.limit);
    const [currPageResults, setCurrPageResults] = React.useState<ModelId[]>([]);
    const [loadingProducts, setLoadingProducts] = React.useState<ModelId[]>([]);
    const [listReady, setListReady] = React.useState(false);

    const fetchProducts = async (config: ProductListConfig) => {
        const serverResponse = await apiGet('productList', undefined, config);
        appStore.dispatch(productListSlice.actions.onResponse(serverResponse));
    };

    React.useEffect(() => {
        setCurrConfig({ ...props.fixedProps });
    }, []);
    React.useEffect(() => {
        if (currConfig !== undefined) {
            setCurrQueryId(idFromConfig(currConfig));
        }
    }, [currConfig]);

    React.useEffect(() => {
        if (currConfig !== undefined && currQueryId !== undefined && !results) {
            fetchProducts(currConfig);
        }
    }, [currQueryId]);

    React.useEffect(() => {
        setListReady(loadingProducts.length === 0);
    }, [loadingProducts]);

    React.useEffect(() => {
        if (Array.isArray(results)) {
            const productIds: ModelId[] = [...results].splice(offset * limit, limit);
            setLoadingProducts(productIds);
            setCurrPageResults(productIds);
        }
    }, [results, offset, limit]);

    const pagination = (): React.ReactNode => {
        if (results && results != 'loading') {
            return <Stack>
                <Pagination size='small' count={Math.ceil(results.length / limit)} page={(offset + 1)} />
                <Typography variant='subtitle1'>{results.length ?? 'aucun'} produit{results.length > 1 ? 's' : ''} trouvé{results.length > 1 ? 's' : ''}</Typography>
            </Stack>;
        } else {
            return null;
        }
    };

    return <MasterViewPage><Container maxWidth={false}>
        {!results || results == 'loading' || !listReady ? <LoadingBackdrop /> : null}
        <Box>
            {pagination()}
            <List>
                {currPageResults.map(id => <ProductListItem key={id} productId={id} loaded={() => {
                    setLoadingProducts(p => p.filter(q => q !== id));
                }} />)}
            </List>
            {pagination()}
        </Box>
    </Container></MasterViewPage>;
};
